import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CountryDropdown } from '@/components/ui/country-dropdown';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { countries } from 'country-data-list';
import { getCountryCallingCode } from 'libphonenumber-js/max';
import { PauseIcon, PhoneIcon, PlayIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type VoiceReceptionist, useVoiceReceptionist } from '../api';
import {
  handoffCountries,
  parseHandoffPhone,
  phoneCountry,
  savedHandoffPhone,
} from './handoff-phone';
import { TestCallForm } from './test-call-form';

const transferCountries = handoffCountries.flatMap((code) =>
  countries.all.filter((country) => country.alpha2 === code)
);

export function VoiceReceptionistCard() {
  const receptionist = useVoiceReceptionist();
  const { query } = receptionist;
  if (query.isLoading) return <Skeleton className="h-40 w-full" />;
  if (!query.data?.visible) return null;
  return (
    <VoiceReceptionistForm settings={query.data} receptionist={receptionist} />
  );
}

function VoiceReceptionistForm({
  settings,
  receptionist,
}: {
  settings: VoiceReceptionist;
  receptionist: ReturnType<typeof useVoiceReceptionist>;
}) {
  const {
    mutation,
    voicesQuery,
    previewVoice,
    saveSettings,
    setEnabled,
    setRecordCalls,
  } = receptionist;
  const [phone, setPhone] = useState(() =>
    savedHandoffPhone(settings.handoffNumber, settings.country)
  );
  const [touched, setTouched] = useState(false);
  const [voiceId, setVoiceId] = useState(
    settings.voiceId ?? settings.defaultVoiceId
  );
  const [playing, setPlaying] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const previewRequest = useRef(0);

  useEffect(
    () => () => {
      previewRequest.current++;
    },
    []
  );

  useEffect(() => {
    setPhone(savedHandoffPhone(settings.handoffNumber, settings.country));
    setTouched(false);
  }, [settings.handoffNumber, settings.country]);
  useEffect(() => {
    setVoiceId(settings.voiceId ?? settings.defaultVoiceId);
  }, [settings.voiceId, settings.defaultVoiceId]);

  const parsed = parseHandoffPhone(phone.number, phone.country);
  const hasNumber = phone.number.trim() !== '';
  const valid = !hasNumber || parsed?.isValid() === true;
  const showPhoneError = !valid && (touched || Boolean(settings.handoffNumber));
  const normalized = hasNumber && parsed?.isValid() ? parsed.number : null;
  const dirty =
    normalized !== settings.handoffNumber ||
    voiceId !== (settings.voiceId ?? settings.defaultVoiceId);
  const voices = voicesQuery.data ?? [];
  const activeVoice = voices.find((voice) => voice.id === voiceId);
  const country = countries.all.find((entry) => entry.alpha2 === phone.country);

  const selectVoice = (value: string) => {
    previewRequest.current++;
    audioRef.current?.pause();
    setVoiceId(value);
    setPlaying(false);
    setLoadingPreview(false);
    setPreviewError(false);
  };

  const changePhone = (value: string) => {
    const incoming = parseHandoffPhone(value, phone.country);
    if (/^(\+|00)/.test(value.trim()) && incoming?.country) {
      setPhone({ country: incoming.country, number: incoming.nationalNumber });
    } else {
      setPhone((current) => ({ ...current, number: value }));
    }
  };
  const playPreview = async () => {
    const audio = audioRef.current;
    if (!audio || !activeVoice) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    const request = ++previewRequest.current;
    setLoadingPreview(true);
    setPreviewError(false);
    try {
      if (audio.dataset.voiceId !== activeVoice.id) {
        const { audio: encoded } = await previewVoice(activeVoice.id);
        if (request !== previewRequest.current || audioRef.current !== audio)
          return;
        audio.src = `data:audio/mpeg;base64,${encoded}`;
        audio.dataset.voiceId = activeVoice.id;
      }
      await audio.play();
      if (request === previewRequest.current) setPlaying(true);
    } catch {
      if (request === previewRequest.current) {
        setPreviewError(true);
        setPlaying(false);
      }
    } finally {
      if (request === previewRequest.current) setLoadingPreview(false);
    }
  };

  return (
    <Card>
      <CardHeader className="grid-rows-1">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <PhoneIcon className="size-4" /> Phone receptionist
          </CardTitle>
          <Switch
            aria-label="Enable phone receptionist"
            title={
              !settings.number && !settings.enabled
                ? 'Set up a receptionist number before enabling calls.'
                : undefined
            }
            checked={settings.enabled}
            disabled={
              mutation.isPending || (!settings.number && !settings.enabled)
            }
            onCheckedChange={setEnabled}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="voice-handoff">
            Put callers through to (Handoff)
          </Label>
          <div
            className="flex h-9 min-w-0 items-stretch rounded-md border border-input bg-transparent shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 aria-invalid:border-destructive"
            role="group"
            aria-label="Transfer phone number"
            aria-invalid={touched && !valid}
          >
            <CountryDropdown
              key={phone.country}
              aria-label="Country calling code"
              defaultValue={country?.alpha3 ?? 'IRL'}
              disabled={mutation.isPending}
              options={transferCountries}
              align="start"
              className="h-full shrink-0 border-0 border-r border-input px-2 shadow-none"
              inline
              onChange={(selected) =>
                setPhone((current) => ({
                  ...current,
                  country: phoneCountry(selected.alpha2),
                }))
              }
            />
            <PhoneInput
              id="voice-handoff"
              aria-describedby="voice-handoff-status voice-handoff-help"
              aria-invalid={showPhoneError}
              className="h-full min-w-0 flex-1 border-0 pl-3 shadow-none has-[input:focus]:ring-0 [&_input]:h-full [&_input]:min-w-0 [&_input]:pl-2 [&_input]:leading-normal"
              callingCode={`+${getCountryCallingCode(phone.country)}`}
              disabled={mutation.isPending}
              value={phone.number}
              onChange={changePhone}
              onBlur={() => {
                setTouched(true);
                if (parsed?.isValid() && parsed.country)
                  setPhone((current) => ({
                    ...current,
                    number: parsed.nationalNumber,
                  }));
              }}
            />
          </div>
          <p id="voice-handoff-help" className="text-xs text-muted-foreground">
            When Claire cannot help. Use a number that does not forward back to
            Claire, such as a mobile.
          </p>
          <p
            id="voice-handoff-status"
            aria-live="polite"
            className={`text-sm ${showPhoneError ? 'font-medium text-destructive' : !hasNumber ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
          >
            {hasNumber && valid
              ? `Valid number: ${normalized}`
              : showPhoneError
                ? 'Enter a valid US, UK or Irish phone number.'
                : 'Optional. Leave blank to take a message instead.'}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="voice-accent">Claire’s accent</Label>
          <div className="flex items-center gap-2">
            <Select
              value={voiceId}
              onValueChange={selectVoice}
              disabled={mutation.isPending || !voices.length}
            >
              <SelectTrigger id="voice-accent" className="h-9 min-w-0 flex-1">
                <SelectValue
                  placeholder={
                    voicesQuery.isPending
                      ? 'Loading accents…'
                      : 'Choose an accent'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {voices.map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.accent} - {voice.name}
                    {voice.region ? ` · ${voice.region}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              className="h-9 w-32 shrink-0"
              disabled={!activeVoice || loadingPreview}
              aria-busy={loadingPreview}
              aria-label={`${playing ? 'Pause' : 'Preview'} ${activeVoice?.accent ?? ''} accent`}
              onClick={() => void playPreview()}
            >
              {playing ? (
                <PauseIcon className="size-4" />
              ) : (
                <PlayIcon className="size-4" />
              )}{' '}
              {loadingPreview ? 'Preparing…' : playing ? 'Pause' : 'Preview'}
            </Button>
            {/* biome-ignore lint/a11y/useMediaCaption: A short spoken voice sample; the control names the accent. */}
            <audio
              ref={audioRef}
              preload="none"
              onEmptied={() => setPlaying(false)}
              aria-label="Accent preview"
              onEnded={() => setPlaying(false)}
              onError={() => {
                setPreviewError(true);
                setPlaying(false);
              }}
            />
          </div>
          {voicesQuery.isError && (
            <p className="text-xs text-destructive" role="alert">
              Could not load accents.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => void voicesQuery.refetch()}
              >
                Try again
              </button>
            </p>
          )}
          {previewError && (
            <p className="text-xs text-destructive" role="alert">
              This preview could not play. Try again or choose another voice.
            </p>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!dirty || !valid || mutation.isPending}
          onClick={() =>
            saveSettings(
              normalized,
              voiceId === (settings.voiceId ?? settings.defaultVoiceId)
                ? settings.voiceId
                : voiceId
            )
          }
        >
          {mutation.isPending ? 'Saving…' : 'Save settings'}
        </Button>

        <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">Record calls</p>
            <p className="text-xs text-muted-foreground">
              Claire tells every caller the call is recorded. Recordings are in
              the inbox with the call, and deleted after 90 days.
            </p>
          </div>
          <Switch
            aria-label="Record calls"
            checked={settings.recordCalls}
            disabled={mutation.isPending}
            onCheckedChange={setRecordCalls}
          />
        </div>
        <TestCallForm
          country={settings.country}
          available={settings.testCallAvailable}
        />
      </CardContent>
    </Card>
  );
}
